import {
  BasketSummaryEarningsCitation,
  BasketSummaryEarningsContent,
} from '@api/api/basket-summary';
import { EarningsItem } from '@api/api/earnings';
import { EarningsInsightRecapResponse } from '@api/api/earnings-insights';
import { KgInsight, KgInsightThemeType } from '@api/api/kg-insights';
import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { generatePath } from '@helpers/utils/paths/paths';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { getUpcomingWeekCutoff } from '@terminal/utils/upcoming-week-cutoff/getUpcomingWeekCutoff';
import { ReportingPeriod } from '@terminal/widgets/earnings/components/reporting-dates/ReportingDates';
import { isSameDay, isWithinInterval, startOfDay } from 'date-fns';
import { getCalendarQuarterInfo } from '../earnings/utils/utils';
import type {
  EarningsTopic,
  GuidanceDirection,
} from './earnings-reporting-summary/types';
import type { SurpriseStat } from './theme-key-stats/utils';

/**
 * Calendar quarter for theme-earnings dropdown/filter state.
 * Distinct from API `fiscal_period` / `fiscal_year` on earnings rows.
 */
export type CalendarQuarterPeriod = {
  quarter: string;
  year: number;
  /** Start of the calendar quarter (ISO); used as the ReportingDates key. */
  reporting_date: string;
};

export type EarningsPresentationVariant = 'page' | 'drawer';

/** Maps into shared ReportingDates, which still uses EarningsPeriod field names. */
export const toReportingDatesPeriod = (
  period: CalendarQuarterPeriod
): ReportingPeriod => ({
  fiscal_period: period.quarter,
  fiscal_year: period.year,
  reporting_date: period.reporting_date,
});

export const fromReportingDatesPeriod = (
  period: ReportingPeriod
): CalendarQuarterPeriod => ({
  quarter: period.fiscal_period,
  year: period.fiscal_year,
  reporting_date: period.reporting_date,
});

/**
 * Fetch range for theme earnings: all available history through the end of
 * the current calendar quarter. The quarter selector is built from whatever
 * periods appear in the response (`extractReportingPeriods`).
 */
export const getSelectableDateRange = () => ({
  startDate: new Date(0),
  endDate: getCalendarQuarterInfo().endDate,
});

export const findReportingPeriod = (
  periods: CalendarQuarterPeriod[],
  selected?: CalendarQuarterPeriod
): CalendarQuarterPeriod | undefined => {
  if (!selected) {
    return periods[0];
  }

  return (
    periods.find(
      period =>
        period.quarter === selected.quarter && period.year === selected.year
    ) ?? periods[0]
  );
};

export const filterEarningsByCalendarPeriod = <
  T extends { reporting_date: string },
>(
  earnings: T[],
  period?: Pick<CalendarQuarterPeriod, 'quarter' | 'year'>
): T[] => {
  if (!period) {
    return [];
  }

  return earnings.filter(earning => {
    const { quarter, year } = getCalendarQuarterInfo(
      new Date(earning.reporting_date)
    );

    return quarter === period.quarter && year === period.year;
  });
};

const getCalendarQuarterIndex = (quarter: string, year: number) =>
  year * 4 + Number(quarter.replace('Q', '')) - 1;

export const isPastReportingPeriod = (
  period: CalendarQuarterPeriod,
  today: Date = new Date()
) => {
  const { quarter, year } = getCalendarQuarterInfo(today);

  return (
    getCalendarQuarterIndex(period.quarter, period.year) <
    getCalendarQuarterIndex(quarter, year)
  );
};

/**
 * How many recaps the summary was actually written from. `source_ids` is the
 * authoritative list; citations only cover recaps the model chose to quote, so
 * they undercount and are a fallback for payloads that omit `source_ids`.
 */
export const getRecapBasisCount = (
  content: BasketSummaryEarningsContent
): number =>
  content.source_ids?.length ??
  new Set(content.citations.map(citation => citation.earnings_id)).size;

export const isReportingDateToday = (
  reportingDate: string,
  today: Date = new Date()
) => isSameDay(new Date(reportingDate), today);

export const isReportingDateThisWeek = (
  reportingDate: string,
  today: Date = new Date()
) =>
  isWithinInterval(new Date(reportingDate), {
    start: startOfDay(today),
    end: getUpcomingWeekCutoff(today),
  });

const byReportingDateAsc = <T extends EarningsItem>(a: T, b: T) =>
  a.reporting_date.localeCompare(b.reporting_date);

/**
 * Reported Today = already reported today.
 * Reporting This Week = not yet reported, scheduled through the week cutoff
 * (includes today's after-close names until `reported` flips).
 */
export const splitReportingSummaryRows = <T extends EarningsItem>(
  earnings: T[],
  today: Date = new Date()
) => ({
  reportedToday: earnings
    .filter(
      earning =>
        earning.reported && isReportingDateToday(earning.reporting_date, today)
    )
    .sort(byReportingDateAsc),
  reportingThisWeek: earnings
    .filter(
      earning =>
        !earning.reported &&
        isReportingDateThisWeek(earning.reporting_date, today)
    )
    .sort(byReportingDateAsc),
});

export const extractReportingPeriods = (
  earnings: EarningsItem[]
): CalendarQuarterPeriod[] => {
  const periodsByQuarter = new Map<string, CalendarQuarterPeriod>();

  for (const { reporting_date } of earnings) {
    const { quarter, year, startDate } = getCalendarQuarterInfo(
      new Date(reporting_date)
    );
    const key = `${quarter} ${year}`;

    if (!periodsByQuarter.has(key)) {
      periodsByQuarter.set(key, {
        quarter,
        year,
        reporting_date: startDate.toISOString(),
      });
    }
  }

  return [...periodsByQuarter.values()].sort((a, b) =>
    b.reporting_date.localeCompare(a.reporting_date)
  );
};

/**
 * Splits reporters into beats (`surprise >= 0`, matching the convention in
 * `EarningsPriceMovementContent`) and misses, then returns the majority
 * side. Items without a surprise value are excluded.
 */
export const getSurpriseStat = (
  earnings: EarningsItem[],
  metric: 'eps' | 'revenue'
): SurpriseStat | undefined => {
  const surprises = earnings
    .map(earning => earning[metric].surprise)
    .filter((surprise): surprise is number => surprise !== undefined);

  if (surprises.length === 0) {
    return undefined;
  }

  const beatPercentage =
    (surprises.filter(surprise => surprise >= 0).length / surprises.length) *
    100;

  return beatPercentage >= 50
    ? { percentage: beatPercentage, beat: true }
    : { percentage: 100 - beatPercentage, beat: false };
};

export const getMedian = (values: number[]): number | undefined => {
  if (values.length === 0) {
    return undefined;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 1
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

export interface GuidanceStat {
  raised: number;
  lowered: number;
  na: number;
}

/**
 * Counts raised / lowered / N/A guidance among reporters. Rows missing from
 * `guidanceByEarningsId` (no matching key-dev event) count as N/A.
 */
export const getGuidanceStat = (
  reportedEarnings: Pick<EarningsItem, 'id'>[],
  guidanceByEarningsId: Map<string, GuidanceDirection>
): GuidanceStat => {
  let raised = 0;
  let lowered = 0;

  for (const { id } of reportedEarnings) {
    const guidance = guidanceByEarningsId.get(id);

    if (guidance === 'raised') {
      raised += 1;
    } else if (guidance === 'lowered') {
      lowered += 1;
    }
  }

  return {
    raised,
    lowered,
    na: reportedEarnings.length - raised - lowered,
  };
};

const OTHER_THEME_TYPE_TO_TOPIC: Record<string, KgInsightThemeType> = {
  Macro: 'macro',
  Financial: 'financial',
};

const KG_KIND_TO_OTHER_THEME_TYPE: Partial<
  Record<KgInsightThemeType, 'Macro' | 'Financial'>
> = {
  macro: 'Macro',
  financial: 'Financial',
};

export type InsightThemesSource = {
  earnings_id: string;
  themes: EarningsInsightRecapResponse['themes'];
  other_themes?: EarningsInsightRecapResponse['other_themes'];
};

const mapRecapToEarningsDrivers = (
  recap: Pick<InsightThemesSource, 'themes' | 'other_themes'>,
  themesById: Map<string, ThemeSchemaResponse>
): EarningsTopic[] => {
  const fromThemes = recap.themes.flatMap(theme => {
    const details = themesById.get(theme.theme_id);
    if (!details) {
      return [];
    }
    return [
      {
        label: details.name,
        themeType: details.type,
        themeId: theme.theme_id,
      },
    ];
  });

  const fromOtherThemes = (recap.other_themes ?? []).flatMap(theme => {
    const themeType = OTHER_THEME_TYPE_TO_TOPIC[theme.type];
    if (!themeType) {
      return [];
    }
    return [{ label: theme.name, themeType }];
  });

  return [...fromThemes, ...fromOtherThemes];
};

const getKgTakeawayTags = (insight: KgInsight) => {
  const takeaway = insight.sections.find(
    section => section.type === 'takeaway'
  );
  if (!takeaway) {
    return [];
  }

  return takeaway.data.cards.flatMap(card => card.tags);
};

export const mapKgInsightToThemesPayload = (
  insight: KgInsight
): InsightThemesSource => {
  const themeIds = new Set<string>();
  const otherThemes: InsightThemesSource['other_themes'] = [];
  const seenOtherThemeKeys = new Set<string>();

  for (const tag of getKgTakeawayTags(insight)) {
    if (tag.id) {
      themeIds.add(tag.id);
      continue;
    }

    const otherType = KG_KIND_TO_OTHER_THEME_TYPE[tag.kind];
    if (!otherType) {
      continue;
    }

    const key = `${otherType}:${tag.label}`;
    if (seenOtherThemeKeys.has(key)) {
      continue;
    }
    seenOtherThemeKeys.add(key);
    otherThemes.push({
      name: tag.label,
      type: otherType,
      rating: 'neutral',
      relationship: '',
    });
  }

  return {
    earnings_id: insight.subject.ref?.id ?? '',
    themes: [...themeIds].map(themeId => ({
      theme_id: themeId,
      rating: 'neutral',
      relationship: '',
    })),
    other_themes: otherThemes,
  };
};

interface ThemesToWatchByEarningsIdArgs {
  insights: InsightThemesSource[];
  themesById: Map<string, ThemeSchemaResponse>;
  themeIdsCount: number;
  isThemesLoading: boolean;
}

/**
 * Insights often resolve before theme details; mapping early drops theme topics,
 * and empty arrays defeat `??` fallbacks in enriched rows.
 */
export const getThemesToWatchByEarningsId = ({
  insights,
  themesById,
  themeIdsCount,
  isThemesLoading,
}: ThemesToWatchByEarningsIdArgs): Map<string, EarningsTopic[]> => {
  const byEarningsId = new Map<string, EarningsTopic[]>();

  if (themeIdsCount > 0 && isThemesLoading) {
    return byEarningsId;
  }

  for (const insight of insights) {
    byEarningsId.set(
      insight.earnings_id,
      mapRecapToEarningsDrivers(insight, themesById)
    );
  }

  return byEarningsId;
};

const TOP_EARNINGS_DRIVERS_COUNT = 5;

const preferTopicWithThemeId = (
  currentTopic: EarningsTopic | undefined,
  nextTopic: EarningsTopic
) => (currentTopic?.themeId ? currentTopic : nextTopic);

export const getTopEarningsDrivers = (
  themesByEarningsId: Map<string, EarningsTopic[]>
): EarningsTopic[] => {
  const countsByTheme = new Map<
    string,
    { count: number; topic: EarningsTopic }
  >();

  for (const themes of themesByEarningsId.values()) {
    const uniqueThemes = new Map<string, EarningsTopic>();
    for (const theme of themes) {
      const key = `${theme.themeType}:${theme.label}`;
      uniqueThemes.set(
        key,
        preferTopicWithThemeId(uniqueThemes.get(key), theme)
      );
    }

    for (const [key, topic] of uniqueThemes) {
      const current = countsByTheme.get(key);
      countsByTheme.set(key, {
        topic: preferTopicWithThemeId(current?.topic, topic),
        count: (current?.count ?? 0) + 1,
      });
    }
  }

  return [...countsByTheme.values()]
    .sort(
      (first, second) =>
        second.count - first.count ||
        first.topic.label.localeCompare(second.topic.label)
    )
    .slice(0, TOP_EARNINGS_DRIVERS_COUNT)
    .map(({ topic }) => topic);
};

const INLINE_CITATION_REGEX = /(?<![!\\])\[(\d+)\](?!\s*(?:\(|:))/g;

const getEarningsCitationHref = (
  citation: BasketSummaryEarningsCitation
): string =>
  generatePath({
    basePath: '',
    params: {
      [queryKeys.activeSubTab]: 'earnings',
      [queryKeys.activeDrawer]: queryValues.earningsInsightDrawer,
      [queryKeys.activeDrawerTab]: 'earnings_recap',
      [queryKeys.entityTag]: citation.entity_tag,
      [queryKeys.earningsId]: citation.earnings_id,
      [queryKeys.insightId]: citation.kg_insight_id,
    },
  });

export interface EarningsCitationDrawerParams {
  entityTag: string;
  earningsId: string;
  insightId: string;
}

export const getEarningsCitationDrawerParams = (
  href: string | null
): EarningsCitationDrawerParams | null => {
  if (!href?.startsWith('?')) {
    return null;
  }

  const params = new URLSearchParams(href.slice(1));
  if (
    params.get(queryKeys.activeDrawer) !== queryValues.earningsInsightDrawer
  ) {
    return null;
  }

  const entityTag = params.get(queryKeys.entityTag);
  const earningsId = params.get(queryKeys.earningsId);
  const insightId = params.get(queryKeys.insightId);
  if (!entityTag || !earningsId || !insightId) {
    return null;
  }

  return { entityTag, earningsId, insightId };
};

export const linkSummaryCitations = (
  summary: string,
  citations: BasketSummaryEarningsCitation[]
): string => {
  const hrefsByCitationId = new Map(
    citations.map(citation => [citation.id, getEarningsCitationHref(citation)])
  );

  return summary.replace(INLINE_CITATION_REGEX, (match, id: string) => {
    const href = hrefsByCitationId.get(Number(id));
    return href ? `[\\[${id}\\]](${href})` : match;
  });
};
